import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, MapPin, Home, Package, Check } from "lucide-react";
import { api, eur } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function Checkout() {
  const { items, total } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: user?.name || "", phone: "", address: "", city: "", zip_code: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [method, setMethod] = useState("pickup");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [points, setPoints] = useState([]);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [pointsOpen, setPointsOpen] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [pointFilter, setPointFilter] = useState("");

  const validZip = /^\d{5}$/.test(form.zip_code);

  const fetchQuote = useCallback(async (zip) => {
    setQuoting(true);
    setQuote(null);
    setSelectedPoint(null);
    try {
      const { data } = await api.get(`/shipping/quote?zip=${zip}`);
      setQuote(data);
      if (!data.pickup && data.home) setMethod("home");
      else if (data.pickup) setMethod("pickup");
    } catch {
      setQuote({ pickup: null, home: null });
    } finally {
      setQuoting(false);
    }
  }, []);

  useEffect(() => {
    if (!validZip) { setQuote(null); setSelectedPoint(null); return; }
    const t = setTimeout(() => fetchQuote(form.zip_code), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.zip_code]);

  const openPoints = async () => {
    if (!quote?.pickup) return;
    setPointsOpen(true);
    setLoadingPoints(true);
    try {
      const { data } = await api.get(`/shipping/pickup-points?zip=${form.zip_code}&service_id=${quote.pickup.service_id}`);
      setPoints(data);
    } catch {
      toast.error("Impossibile caricare i punti di ritiro");
      setPoints([]);
    } finally {
      setLoadingPoints(false);
    }
  };

  const choosePoint = (p) => { setSelectedPoint(p); setPointsOpen(false); };

  const shippingCost = quote && (method === "pickup" ? quote.pickup?.price : quote.home?.price);
  const grandTotal = total + (shippingCost || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Il tuo scrigno è vuoto"); return; }
    if (!form.full_name.trim()) { toast.error("Inserisci il nome completo"); return; }
    if (!form.phone.trim()) { toast.error("Inserisci un numero di telefono"); return; }
    if (!validZip) { toast.error("Inserisci un CAP valido"); return; }
    if (!form.city.trim()) { toast.error("Inserisci la città"); return; }
    if (!quote || (!quote.pickup && !quote.home)) { toast.error("Spedizione non disponibile per questo CAP"); return; }
    if (method === "pickup" && !selectedPoint) { toast.error("Scegli un punto di ritiro InPost"); return; }
    if (method === "home" && !form.address.trim()) { toast.error("Inserisci l'indirizzo di consegna"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        full_name: form.full_name, phone: form.phone,
        address: method === "pickup" ? (selectedPoint?.address || "") : form.address,
        city: method === "pickup" ? (selectedPoint?.city || form.city) : form.city,
        zip_code: form.zip_code,
        delivery_method: method,
        pickup_point: method === "pickup" && selectedPoint ? {
          id: selectedPoint.id, name: selectedPoint.name, address: selectedPoint.address,
          city: selectedPoint.city, zip: selectedPoint.zip,
        } : null,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile avviare il pagamento. Riprova.");
      setLoading(false);
    }
  };

  return (
    <div className="relative z-10 pt-32 pb-24 max-w-[1400px] mx-auto px-6 md:px-10">
      <h1 className="font-serif text-5xl md:text-7xl font-light mb-16" data-testid="checkout-title">Cassa</h1>
      <div className="grid md:grid-cols-5 gap-16">
        <form onSubmit={submit} noValidate className="md:col-span-3 space-y-10" data-testid="checkout-form">
          {/* Contatti */}
          <div>
            <h2 className="font-serif text-2xl mb-6">I tuoi dati</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div><Label>Nome completo</Label><Input required value={form.full_name} onChange={set("full_name")} className="mt-2" data-testid="input-fullname" /></div>
              <div><Label>Telefono</Label><Input required value={form.phone} onChange={set("phone")} placeholder="+39 ..." className="mt-2" data-testid="input-phone" /></div>
              <div><Label>CAP</Label><Input required value={form.zip_code} onChange={set("zip_code")} maxLength={5} placeholder="Es. 31021" className="mt-2" data-testid="input-zip" /></div>
              <div><Label>Città</Label><Input required value={form.city} onChange={set("city")} className="mt-2" data-testid="input-city" /></div>
            </div>
          </div>

          {/* Consegna */}
          <div>
            <h2 className="font-serif text-2xl mb-6">Metodo di consegna</h2>
            {!validZip && <p className="text-sm text-muted-foreground" data-testid="shipping-hint">Inserisci il tuo CAP per vedere le opzioni di consegna.</p>}
            {validZip && quoting && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm" data-testid="shipping-loading"><Loader2 size={16} className="animate-spin" /> Calcolo spedizione…</div>
            )}
            {validZip && !quoting && quote && !quote.pickup && !quote.home && (
              <p className="text-sm text-destructive" data-testid="shipping-none">Nessuna spedizione disponibile per questo CAP.</p>
            )}
            {validZip && !quoting && quote && (quote.pickup || quote.home) && (
              <RadioGroup value={method} onValueChange={setMethod} className="space-y-3" data-testid="delivery-method">
                {quote.pickup && (
                  <label className={`flex items-start gap-4 border rounded-lg p-5 cursor-pointer transition-colors ${method === "pickup" ? "border-gold bg-gold/5" : "border-border"}`} data-testid="method-pickup">
                    <RadioGroupItem value="pickup" id="m-pickup" className="mt-1" data-testid="radio-pickup" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium flex items-center gap-2"><MapPin size={17} className="text-gold" /> Punto di ritiro InPost</span>
                        <span className="font-serif text-lg">{eur(quote.pickup.price)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">Ritira il tuo ordine nel locker/punto InPost più comodo per te.</p>
                      {method === "pickup" && (
                        <div className="mt-4">
                          {selectedPoint ? (
                            <div className="text-sm bg-secondary/50 rounded-md p-3" data-testid="selected-point">
                              <p className="font-medium">{selectedPoint.name}</p>
                              <p className="text-muted-foreground">{selectedPoint.address}, {selectedPoint.city}</p>
                              <button type="button" onClick={openPoints} className="text-gold underline mt-2" data-testid="change-point-btn">Cambia punto</button>
                            </div>
                          ) : (
                            <Button type="button" variant="outline" onClick={openPoints} className="rounded-full" data-testid="choose-point-btn">
                              <MapPin size={15} className="mr-2" /> Scegli punto di ritiro
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </label>
                )}
                {quote.home && (
                  <label className={`flex items-start gap-4 border rounded-lg p-5 cursor-pointer transition-colors ${method === "home" ? "border-gold bg-gold/5" : "border-border"}`} data-testid="method-home">
                    <RadioGroupItem value="home" id="m-home" className="mt-1" data-testid="radio-home" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium flex items-center gap-2"><Home size={17} className="text-gold" /> Consegna a domicilio</span>
                        <span className="font-serif text-lg">{eur(quote.home.price)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">Consegna direttamente al tuo indirizzo{quote.home.carrier ? ` · ${quote.home.carrier}` : ""}.</p>
                      {method === "home" && (
                        <div className="mt-4">
                          <Label>Indirizzo di consegna</Label>
                          <Input required={method === "home"} value={form.address} onChange={set("address")} placeholder="Via e numero civico" className="mt-2" data-testid="input-address" />
                        </div>
                      )}
                    </div>
                  </label>
                )}
              </RadioGroup>
            )}
          </div>

          <div className="bg-secondary/40 border border-border rounded-lg p-6 flex items-start gap-3">
            <Lock size={18} className="mt-1 text-gold shrink-0" />
            <div>
              <p className="font-medium">Pagamento sicuro con Stripe</p>
              <p className="text-sm text-muted-foreground mt-1">Verrai reindirizzato a una pagina protetta dove potrai pagare con carta, inclusa la tua Postepay.</p>
            </div>
          </div>

          <Button type="submit" disabled={loading || !validZip || !quote || (quote && !quote.pickup && !quote.home)} className="w-full rounded-full bg-gold hover:bg-gold/90 text-white h-14 text-base" data-testid="place-order-btn">
            {loading ? "Reindirizzamento…" : `Vai al pagamento · ${eur(grandTotal)}`}
          </Button>
        </form>

        {/* Riepilogo */}
        <div className="md:col-span-2">
          <div className="bg-secondary/40 border border-border rounded-lg p-8 sticky top-28">
            <h2 className="font-serif text-2xl mb-6">Il tuo ordine</h2>
            <div className="space-y-4 mb-6">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-3 text-sm" data-testid={`summary-item-${i.product_id}`}>
                  <img src={i.image} alt="" className="h-16 w-12 object-cover rounded" />
                  <div className="flex-1"><p className="font-serif text-base">{i.name}</p><p className="text-muted-foreground">×{i.quantity}</p></div>
                  <span>{eur(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4 space-y-2">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotale</span><span data-testid="checkout-subtotal">{eur(total)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Spedizione</span><span data-testid="checkout-shipping">{shippingCost != null ? eur(shippingCost) : "—"}</span></div>
              <div className="flex justify-between text-lg pt-2"><span className="text-muted-foreground">Totale</span><span className="font-serif text-2xl" data-testid="checkout-total">{eur(grandTotal)}</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog punti di ritiro */}
      <Dialog open={pointsOpen} onOpenChange={setPointsOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="points-dialog">
          <DialogHeader><DialogTitle className="font-serif flex items-center gap-2"><Package size={20} className="text-gold" /> Punti di ritiro InPost</DialogTitle></DialogHeader>
          <DialogDescription>Scegli il punto di ritiro InPost più comodo vicino al CAP {form.zip_code}.</DialogDescription>
          <Input value={pointFilter} onChange={(e) => setPointFilter(e.target.value)} placeholder="Cerca per nome o indirizzo…" className="mt-1" data-testid="point-search" />
          <div className="overflow-y-auto -mx-2 px-2 mt-2 space-y-2">
            {loadingPoints && <div className="flex items-center gap-2 text-muted-foreground text-sm py-6 justify-center"><Loader2 size={16} className="animate-spin" /> Caricamento punti…</div>}
            {!loadingPoints && points.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessun punto trovato per questo CAP.</p>}
            {!loadingPoints && points
              .filter((p) => {
                const q = pointFilter.trim().toLowerCase();
                if (!q) return true;
                return ((p.name || "") + " " + (p.address || "") + " " + (p.city || "")).toLowerCase().includes(q);
              })
              .map((p) => (
              <button type="button" key={p.id} onClick={() => choosePoint(p)} className="w-full text-left border border-border rounded-lg p-4 hover:border-gold hover:bg-gold/5 transition-colors flex items-start justify-between gap-3" data-testid={`point-${p.id}`}>
                <div>
                  <p className="font-medium text-sm">{p.name || "Punto InPost"}</p>
                  <p className="text-xs text-muted-foreground mt-1">{p.address}, {p.city} {p.zip}</p>
                </div>
                {selectedPoint?.id === p.id && <Check size={18} className="text-gold shrink-0" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
